import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { PoPermissionsConfigService } from '../../services/po-permissions-config.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import {
  CatalogItem,
  FinancesConfig,
  NewPurchaseOrderConfig,
  PurchaseOrderComment,
  PurchaseOrderDetail,
} from '../../models/makers-finances-mgr.models';
import {
  PO_LABEL_TO_CODE, PO_EMPTY_VALUE,
  PO_FIELD, SOURCE_TYPE, DETAIL_MODE,
  PO_DIALOG_TYPE,
  PO_ACTION, PO_STATUS_BANNER,
} from '../purchase-order-detail/purchase-order.constants';
import { UploadedDocumentValue } from '../purchase-order-detail/sections/section-po-document/section-po-document.component';
import { PoActionDialogComponent } from '../po-action-dialog/po-action-dialog.component';
import { PoConfirmDialogComponent } from '../purchase-order-detail/po-confirm-dialog/po-confirm-dialog.component';
import { STORAGE_KEY } from '../../../definitions/app.constants';

interface ViewComment {
  id: number;
  author: string;
  initials: string;
  date: string;
  text: string;
}

@Component({
  selector: 'app-purchase-order-view',
  templateUrl: './purchase-order-view.component.html',
  styleUrls: ['./purchase-order-view.component.scss']
})
export class PurchaseOrderViewComponent implements OnInit {
  loading = true;
  po: PurchaseOrderDetail | null = null;
  newComment = '';

  // Exposed for the template so empty fields render a placeholder instead of disappearing.
  readonly EMPTY_VALUE = PO_EMPTY_VALUE;
  readonly SOURCE_TYPE = SOURCE_TYPE;

  // Resolved names for IDs returned by the detail endpoint
  sourceTypeName = '';
  sourceTypeCode = '';
  poTypeName = '';
  statusCode: string | null = null;
  comments: ViewComment[] = [];
  private remoteConfig: NewPurchaseOrderConfig | null = null;
  private statusBanners: Partial<Record<string, { message: string }>> = {};
  whitelistApprovers: Record<string, string[]> | null = null;
  userEmail = '';

  // Minimal form for app-section-po-document in view mode — only carries the uploaded doc value.
  docForm = new FormGroup({
    [PO_FIELD.DOCUMENT_FILE]: new FormControl(null),
  });
  readonly DOC_SOURCE_TYPE = SOURCE_TYPE.NONE;
  readonly DOC_MODE = DETAIL_MODE.EDIT;

  private readonly sourceTypeNameById = new Map<number, string>();
  private readonly poTypeNameById = new Map<number, string>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly financesService: MakersFinancesMgrService,
    private readonly permissionsConfig: PoPermissionsConfigService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
  ) {}

  get canEdit(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.EDIT);
  }

  get canCancel(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.CANCEL_PO);
  }

  get canClose(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.CLOSE_PO);
  }

  get canReopen(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.REOPEN_PO);
  }

  get canComment(): boolean {
    return !this.permissionsConfig.isCommonActionHidden('comment', this.statusCode);
  }

  get statusBanner(): { bg: string; iconColor: string; message: string } | null {
    if (!this.statusCode) {
      return null;
    }
    const colors = PO_STATUS_BANNER[this.statusCode];
    if (!colors) {
      return null;
    }
    const message = this.statusBanners[this.statusCode]?.message;
    if (!message) {
      return null;
    }
    return { bg: colors.bg, iconColor: colors.iconColor, message };
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || !Number.isFinite(id)) {
      this.alertsService.openAlerts('Invalid purchase order id.', AlertsType.warning);
      this.goBack();
      return;
    }

    forkJoin({
      po: this.financesService.getPurchaseOrder(id),
      sourceTypes: this.financesService.getSourceTypes(),
      poTypes: this.financesService.getPoTypeOptions(),
    }).pipe(
      catchError(err => {
        this.loading = false;
        const status = err?.status;
        if (status === 401) {
          this.alertsService.openAlerts('You are not authorized to view this purchase order.', AlertsType.warning);
        } else if (status === 404) {
          this.alertsService.openAlerts('Purchase order not found.', AlertsType.warning);
        } else {
          this.alertsService.openAlerts('Error loading purchase order. Please try again.', AlertsType.error);
        }
        this.goBack();
        return of(null);
      }),
    ).subscribe(result => {
      if (!result) {
        return;
      }
      this.loading = false;
      this.po = result.po.data;

      result.sourceTypes.data.records.forEach((r: CatalogItem) => this.sourceTypeNameById.set(r.id, r.name));
      result.poTypes.data.records.forEach((r: CatalogItem) => this.poTypeNameById.set(r.id, r.name));

      const sourceTypeId = this.po.source_type_id;
      const sourceRecord = sourceTypeId != null
        ? result.sourceTypes.data.records.find((r: CatalogItem) => r.id === sourceTypeId)
        : null;
      this.sourceTypeCode = sourceRecord?.code ?? '';
      this.sourceTypeName = sourceRecord?.name ?? '';
      this.poTypeName = this.po.po_type_id != null
        ? (this.poTypeNameById.get(this.po.po_type_id) ?? '')
        : '';
      this.statusCode = this.po.stage ? PO_LABEL_TO_CODE[this.po.stage] ?? null : null;
      this.comments = this.po.comments.map(c => this.toViewComment(c));

      if (this.po.po_document_key) {
        this.docForm.get(PO_FIELD.DOCUMENT_FILE)?.setValue({
          key: this.po.po_document_key,
          name: this.po.po_document_file_name ?? '',
          uploadedAt: this.po.po_document_uploaded_at ?? '',
        });
      }
    });

    this.financesService.config$
      .pipe(filter(c => !!c), take(1))
      .subscribe((config: FinancesConfig) => {
        this.remoteConfig = config.new_puchase_order;
        this.statusBanners = config.purchase_order_details?.status_banners ?? {};
        this.whitelistApprovers = config.purchase_order_whitelist_approvers ?? null;
      });

    this.userEmail = localStorage.getItem(STORAGE_KEY.email) ?? '';

    this.permissionsConfig.loadConfig().pipe(take(1)).subscribe();
  }

  formatCurrency(amount: number | null, currency: string | null): string {
    if (amount == null) {
      return '';
    }
    const locale = currency === 'BRL' ? 'pt-BR' : currency === 'MXN' ? 'es-MX' : 'es-CO';
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  formatDateTime(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  getInitials(author: string): string {
    if (!author) {
      return '';
    }
    const parts = author.split(/[\s@.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return author.slice(0, 2).toUpperCase();
  }

  getInitialsColor(initials: string): string {
    const colors = ['#E65100', '#1565C0', '#2E7D32', '#6A1B9A', '#AD1457', '#00695C'];
    const idx = (initials.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  }

  addComment(): void {
    if (!this.po || !this.newComment.trim()) {
      return;
    }
    this.financesService.addComment(this.po.id, this.newComment.trim())
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.newComment = '';
          this.alertsService.openAlerts('Comment added successfully.', AlertsType.success);
          this._refreshDetails();
        },
        error: (err) => this._handlePoActionError(err, 'adding comment'),
      });
  }

  attachFile(): void {
    // TODO: file attachment not yet implemented
  }

  resubmitForApproval(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: {
        title: 'Submit form?',
        message: 'Please check the information, changes are not allowed until approval.',
        confirmLabel: 'Yes, send request',
      },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || !this.po) {
        return;
      }
      this.financesService.resubmitPurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order resubmitted successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'resubmitting'),
        });
    });
  }

  retrySettlement(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: {
        title: 'Retry settlement?',
        message: 'This will retry the settlement process for this purchase order.',
        confirmLabel: 'Yes, retry settlement',
      },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || !this.po) {
        return;
      }
      this.financesService.retrySettlement(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Settlement registration retried successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'retrying settlement'),
        });
    });
  }

  retryTrato(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: {
        title: 'Retry Trato?',
        message: 'This will retry the Trato contract creation for this purchase order.',
        confirmLabel: 'Yes, retry Trato',
      },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || !this.po) {
        return;
      }
      this.financesService.retryTrato(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Trato contract creation retried successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'retrying Trato'),
        });
    });
  }

  approvePurchaseOrder(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.APPROVE },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.approvePurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order approved successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'approving'),
        });
    });
  }

  returnToKam(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.RETURN_TO_KAM },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.returnToKam(this.po.id, result.reason || '')
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order returned to KAM successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'returning to KAM'),
        });
    });
  }

  // Header action buttons (see Figma node 517:47206). Edit navigates to the PO detail form in
  // edit mode; Close (trash icon) closes/cancels the PO. Both are TODO until the close endpoint
  // lands in the contract — edit is wired now since the detail route already supports edit mode.
  editOrder(): void {
    if (!this.po) {
      return;
    }
    this.router.navigate(['/financial-hub/purchase-orders', this.po.id, 'edit']);
  }

  lockOrder(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.LOCK },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.closePurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order closed successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'closing'),
        });
    });
  }

  unlockOrder(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.UNLOCK },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.reopenPurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order reopened successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'reopening'),
        });
    });
  }

  private _handlePoActionError(err: any, action: string): void {
    const status = err?.status;
    const serverMessage = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null);
    if (serverMessage) {
      this.alertsService.openAlerts(serverMessage, AlertsType.warning);
    } else if (status === 404) {
      this.alertsService.openAlerts('Purchase order not found.', AlertsType.warning);
    } else {
      this.alertsService.openAlerts(
        `Error ${action} purchase order. Please try again.`,
        AlertsType.error,
      );
    }
  }

  closeOrder(): void {
    if (!this.po) {
      return;
    }

    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.CANCEL },
    });

    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }

      this.financesService.cancelPurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts(
              'Purchase order canceled successfully.',
              AlertsType.success,
            );
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'canceling'),
        });
    });
  }

  goBack(): void {
    this.router.navigate(['/financial-hub/purchase-orders']);
  }

  private _refreshDetails(): void {
    if (!this.po) {
      return;
    }
    this.financesService.getPurchaseOrder(this.po.id)
      .pipe(take(1))
      .subscribe(res => {
        this.po = res.data;
        this.statusCode = this.po.stage ? PO_LABEL_TO_CODE[this.po.stage] ?? null : null;
        this.comments = this.po.comments.map(c => this.toViewComment(c));
      });
  }

  private toViewComment(c: PurchaseOrderComment): ViewComment {
    return {
      id: c.id,
      author: c.author,
      initials: this.getInitials(c.author),
      date: this.formatDateTime(c.created_at),
      text: c.content,
    };
  }

  // Names resolved from nested objects returned by the detail endpoint.
  get companyToBillName(): string {
    return this.po?.company_to_bill?.name ?? '';
  }

  get contactName(): string {
    const c = this.po?.contact;
    if (!c) {
      return '';
    }
    return c.email ? `${c.name}  ·  ${c.email}` : c.name;
  }

  get agencyName(): string {
    return this.po?.agency?.name ?? '';
  }

  get revenueRecognitionMethodLabel(): string {
    const method = this.po?.revenue_recognition_method;
    if (!method) {
      return '';
    }
    const options = this.remoteConfig?.agency_recognition_method_options;
    if (!options?.length) {
      return method;
    }
    return options.find(o => o.value === method)?.label ?? method;
  }

  get requestReasonLabel(): string {
    const reason = this.po?.request_reason;
    if (!reason) {
      return '';
    }
    const options = this.remoteConfig?.request_reason_options;
    if (!options?.length) {
      return reason;
    }
    return options.find(o => o.code === reason)?.name ?? reason;
  }
}
